import { type BTFileSystem } from "legacyComponents/FileUploadContainer.types";
import { type FunctionComponent, type ReactNode, useCallback, useRef, useState } from "react";

import { BTDropdown } from "@btnet/core-components";

import { BdsButton, BdsIconDotsThreeVertical } from "@buildertrend/components";

import { track } from "utilities/analytics/analytics";
import { addCloseOnScroll, removeCloseOnScroll } from "utilities/helpers";

import { BTIconEllipsisOutlined } from "commonComponents/btWrappers/BTIcon";

import "./MediaContextMenu.less";

interface IMediaContextMenuProps {
    file: BTFileSystem;
    renderOverlay: (menuItemClick: () => void) => JSX.Element;
    iconSize?: number;
    children?: ReactNode;
}

const MediaContextMenuIcon: FunctionComponent<{ className?: string; size?: number }> = ({
    className,
    size,
}) => <BTIconEllipsisOutlined className={className} rotate={90} size={size} />;

export const HiddenMediaContextMenu: FunctionComponent<{ iconSize?: number }> = ({ iconSize }) => (
    <MediaContextMenuIcon className="invisible" size={iconSize} />
);

export const MediaContextMenu = track(() => ({
    extraInfo: { component: "MediaContextMenu" },
}))(function ({ children, file, renderOverlay }: IMediaContextMenuProps) {
    const dropdownMenuSpan = useRef<HTMLSpanElement | null>(null);
    const [isOpen, setIsOpen] = useState(false);

    const handleScroll = useCallback(() => {
        setIsOpen(false);
    }, []);

    const handleVisibleChange = useCallback(
        (visible: boolean) => {
            setIsOpen(visible);

            if (dropdownMenuSpan.current) {
                if (visible) {
                    addCloseOnScroll(dropdownMenuSpan.current, handleScroll);
                } else {
                    removeCloseOnScroll(dropdownMenuSpan.current, handleScroll);
                }
            }
        },
        [handleScroll],
    );
    const handleClose = useCallback(() => {
        handleVisibleChange(false);
    }, [handleVisibleChange]);

    const handleDropdownButtonClick = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            handleVisibleChange(!isOpen);
        },
        [handleVisibleChange, isOpen],
    );

    const dropdownOverlay = renderOverlay(handleClose);

    return (
        <>
            <span ref={dropdownMenuSpan} />
            <BTDropdown
                testid={`media-context-menu-dropdown-${file.id}`}
                overlay={dropdownOverlay}
                trigger={children ? ["contextMenu"] : ["click"]}
                visible={isOpen}
                onVisibleChange={handleVisibleChange}
                destroyPopupOnHide
                className="MediaContextMenuDropdown"
                overlayClassName="MediaContextMenuOverlay"
            >
                {children || (
                    <div
                        className="flex justify-content-center"
                        onContextMenu={handleClose}
                        data-testid={`file-context-menu-${file.id}`}
                    >
                        <BdsButton
                            ariaLabel="More files"
                            icon={<BdsIconDotsThreeVertical />}
                            testid={`file-context-menu-btn-${file.id}`}
                            displayType="tertiary"
                            onClick={handleDropdownButtonClick}
                        />
                    </div>
                )}
            </BTDropdown>
        </>
    );
});
